import React, { useEffect, useRef, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  ButtonGroup,
  IconButton,
  Tooltip,
  Typography,
  Chip,
  Stack,
  Divider,
  Checkbox,
  FormGroup
} from '@mui/material';
import {
  VisibilityOff,
  CenterFocusStrong,
  FitScreen,
  Settings,
  Map as MapIcon
} from '@mui/icons-material';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useAPI } from '../../hooks/useAPI';

// Utility function to format time ago
const formatTimeAgo = (timestamp: string | Date): string => {
  const now = new Date();
  const lastUpdate = new Date(timestamp);
  const diffInSeconds = Math.floor((now.getTime() - lastUpdate.getTime()) / 1000);

  if (diffInSeconds < 5) return 'just now';
  if (diffInSeconds < 60) return `${diffInSeconds}s ago`;

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}h ago`;

  const diffInDays = Math.floor(diffInHours / 24);
  return `${diffInDays}d ago`;
};

// Utility function to determine tracker status based on data freshness
const getTrackerStatus = (lastUpdate: string | Date, originalStatus?: string): 'active' | 'standby' | 'offline' => {
  const now = new Date();
  const lastUpdateTime = new Date(lastUpdate);
  const diffInMinutes = Math.floor((now.getTime() - lastUpdateTime.getTime()) / (1000 * 60));

  // If more than 30 minutes without data, consider offline
  if (diffInMinutes >= 30) {
    return 'offline';
  }

  // If more than 5 minutes without data, consider standby
  if (diffInMinutes >= 5) {
    return 'standby';
  }

  // Recent data, keep as active
  return 'active';
};

interface SpeedMetrics {
  current: number;
  average: number;
  max: number;
  smoothed: number;
}

interface ElevationMetrics {
  current: number;
  gain: number;
  loss: number;
  gradient: number;
  isClimbing: boolean;
}

interface TrackerData {
  trackerId: string;
  currentRole: string;
  title: string;
  position: {
    lat: number;
    lng: number;
  };
  speed: SpeedMetrics | number;
  elevation: ElevationMetrics | number;
  battery?: number;
  batteryLevel?: number;
  signalBars?: number;
  signalStrength?: number;
  networkType?: string;
  lastUpdate: string;
  status: 'active' | 'standby' | 'offline';
}

interface ActiveRoute {
  raceId: string;
  totalDistance: number;
  raceName?: string;
  stageName?: string;
  hasCoordinates?: boolean;
}

interface DashboardData {
  trackers: TrackerData[];
  activeRoute?: ActiveRoute;
}

// Set Mapbox access token
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || '';

// Helper functions to extract values from metrics
const getSpeedValue = (speed: SpeedMetrics | number): number => {
  return typeof speed === 'object' ? speed.current : speed;
};

const getElevationValue = (elevation: ElevationMetrics | number): number => {
  return typeof elevation === 'object' ? elevation.current : elevation;
};

// Create pulsing dot animation for tracker beacons
const createPulsingDot = (color: string, size: number = 200) => {
  const pulsingDot = {
    width: size,
    height: size,
    data: new Uint8Array(size * size * 4),
    context: null as CanvasRenderingContext2D | null,

    onAdd: function () {
      const canvas = document.createElement('canvas');
      canvas.width = this.width;
      canvas.height = this.height;
      this.context = canvas.getContext('2d');
    },

    render: function () {
      const duration = 1000;
      const t = (performance.now() % duration) / duration;

      const radius = (size / 2) * 0.3;
      const outerRadius = (size / 2) * 0.7 * t + radius;
      const context = this.context;

      if (!context) return false;

      // Clear the canvas
      context.clearRect(0, 0, this.width, this.height);

      // Draw the outer circle with fade effect
      context.beginPath();
      context.arc(this.width / 2, this.height / 2, outerRadius, 0, Math.PI * 2);
      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);
      context.fillStyle = `rgba(${r}, ${g}, ${b}, ${1 - t})`;
      context.fill();

      // Draw the inner circle with pulsing border
      context.beginPath();
      context.arc(this.width / 2, this.height / 2, radius, 0, Math.PI * 2);
      context.fillStyle = color;
      context.strokeStyle = 'white';
      context.lineWidth = 3 + 6 * (1 - t);
      context.fill();
      context.stroke();

      this.data = new Uint8Array(context.getImageData(0, 0, this.width, this.height).data);
      return true;
    }
  };

  return pulsingDot;
};

// Create start/finish marker element for routes
const createRouteMarkerElement = (type: 'start' | 'finish'): HTMLElement => {
  const el = document.createElement('div');
  const circleColor = type === 'start' ? '#22C55E' : '#000000';

  el.style.cssText = `
    background-color: ${circleColor};
    border: 3px solid white;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 12px;
    font-weight: bold;
    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    cursor: pointer;
    user-select: none;
  `;
  el.textContent = type === 'start' ? 'S' : 'F';
  return el;
};

const BroadcastMap: React.FC = () => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const routeMarkers = useRef<{ start?: mapboxgl.Marker; finish?: mapboxgl.Marker }>({});

  // Data state
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);

  // API hook
  const api = useAPI();

  // Camera control state
  const [autoCameraMode, setAutoCameraMode] = useState(true);
  const [followTracker, setFollowTracker] = useState<string>('all');

  // UI control state
  const [showControls, setShowControls] = useState(false);
  const [showTrackerPanel, setShowTrackerPanel] = useState(false);

  // Tracker visibility state
  const [trackerVisibility, setTrackerVisibility] = useState<Record<string, boolean>>({});

  const trackers = useMemo(() => dashboardData?.trackers || [], [dashboardData?.trackers]);
  const activeRoute = dashboardData?.activeRoute;

  // Fetch dashboard data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.get('/dashboard/');
        setDashboardData(response.data.data);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 1000); // Update every second

    return () => clearInterval(interval);
  }, [api]);

  // Initialize tracker visibility when trackers change
  useEffect(() => {
    setTrackerVisibility(prevVisibility => {
      const newVisibility = { ...prevVisibility };
      trackers.forEach((tracker: TrackerData) => {
        if (!(tracker.trackerId in newVisibility)) {
          newVisibility[tracker.trackerId] = true; // Default to visible
        }
      });
      return newVisibility;
    });
  }, [trackers]);

  // Memoize the route ID to prevent unnecessary re-renders
  const stableRouteId = useMemo(() => {
    return activeRoute?.raceId || null;
  }, [activeRoute?.raceId]);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    const initializeMap = () => {
      if (!mapContainer.current) {
        setTimeout(initializeMap, 100);
        return;
      }

      try {
        map.current = new mapboxgl.Map({
          container: mapContainer.current!,
          style: 'mapbox://styles/wedobroadcast/cmftsafww00j801sd8ifhcmir',
          center: [101.9758, 4.2105], // Center of Malaysia
          zoom: 6,
          attributionControl: false,
        });

        map.current.on('load', () => {
          console.log('Broadcast map loaded successfully');

          // Add pulsing dot images for tracker status
          const statusColors = {
            active: '#3b82f6',    // Blue
            standby: '#f59e0b',   // Amber
            offline: '#94a3b8'    // Gray
          };

          Object.entries(statusColors).forEach(([status, color]) => {
            const pulsingDot = createPulsingDot(color, 200);
            map.current!.addImage(`beacon-${status}`, pulsingDot, { pixelRatio: 2 });
          });

          // Add source for tracker points
          map.current!.addSource('trackers', {
            type: 'geojson',
            data: {
              type: 'FeatureCollection',
              features: []
            }
          });

          // Add layer for tracker symbols
          map.current!.addLayer({
            id: 'tracker-symbols',
            type: 'symbol',
            source: 'trackers',
            layout: {
              'icon-image': ['get', 'icon'],
              'icon-size': 0.6,
              'icon-allow-overlap': true,
              'icon-ignore-placement': true,
              'text-field': ['get', 'label'],
              'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
              'text-size': 12,
              'text-offset': [0, 2.2],
              'text-anchor': 'top'
            },
            paint: {
              'text-color': '#000000',
              'text-halo-width': 0
            }
          });

          // Add click handler for popups
          map.current!.on('click', 'tracker-symbols', (e) => {
            if (e.features && e.features.length > 0) {
              const feature = e.features[0];
              const coordinates = (feature.geometry as any).coordinates.slice();
              const properties = feature.properties!;

              const popupContent = `
                <div style="width: 280px; padding: 12px; font-family: Roboto, sans-serif; background-color: white; color: #333; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);">
                  <h3 style="margin: 0 0 8px 0; font-size: 1.1rem; font-weight: 600; color: #333;">
                    ${properties.label}
                  </h3>
                  <p style="margin: 0 0 12px 0; font-size: 0.75rem; color: #666;">
                    ID: ${properties.trackerId}
                  </p>
                  <div style="margin-bottom: 8px;">
                    <span style="font-size: 0.85rem; color: #333;">
                      📍 ${coordinates[1].toFixed(4)}, ${coordinates[0].toFixed(4)}
                    </span>
                  </div>
                  <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
                    <span style="font-size: 0.85rem; color: #333;">⚡ ${properties.speed.toFixed(1)} km/h</span>
                    <span style="font-size: 0.85rem; color: #333;">⛰️ ${properties.elevation.toFixed(0)}m</span>
                  </div>
                  <div style="display: flex; justify-content: space-between; margin-bottom: 12px;">
                    <span style="font-size: 0.85rem; color: #333;">🔋 ${properties.battery}%</span>
                    <span style="font-size: 0.85rem; color: #333;">📶 ${properties.signal}/5</span>
                  </div>
                  <div style="display: flex; justify-content: space-between; align-items: center;">
                    <span style="background-color: ${properties.status === 'active' ? '#22c55e' : properties.status === 'standby' ? '#f59e0b' : '#ef4444'}; color: white; padding: 4px 8px; border-radius: 12px; font-size: 0.7rem; text-transform: uppercase; font-weight: 600;">
                      ${properties.status}
                    </span>
                    <span style="font-size: 0.7rem; color: #666;">
                      ${formatTimeAgo(properties.lastUpdate)}
                    </span>
                  </div>
                </div>
              `;

              new mapboxgl.Popup({
                className: 'dark-popup'
              })
                .setLngLat(coordinates)
                .setHTML(popupContent)
                .addTo(map.current!);
            }
          });

          // Cursor changes
          map.current!.on('mouseenter', 'tracker-symbols', () => {
            if (map.current) map.current.getCanvas().style.cursor = 'pointer';
          });

          map.current!.on('mouseleave', 'tracker-symbols', () => {
            if (map.current) map.current.getCanvas().style.cursor = '';
          });
        });

      } catch (error) {
        console.error('Error initializing broadcast map:', error);
      }
    };

    initializeMap();

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  // Update trackers when data changes or visibility changes
  useEffect(() => {
    if (!map.current || !map.current.isStyleLoaded()) return;

    // Filter trackers based on visibility settings and compute status
    const visibleTrackers = trackers
      .filter((tracker: TrackerData) =>
        tracker.position &&
        tracker.position.lat &&
        tracker.position.lng &&
        trackerVisibility[tracker.trackerId] !== false
      )
      .map((tracker: TrackerData) => {
        // Compute actual status based on data freshness
        const computedStatus = tracker.lastUpdate
          ? getTrackerStatus(tracker.lastUpdate, tracker.status)
          : 'offline';

        return {
          ...tracker,
          status: computedStatus
        };
      })
      .filter((tracker: TrackerData) => tracker.status !== 'offline'); // Hide offline trackers

    // Create GeoJSON features for visible trackers
    const features = visibleTrackers.map((tracker: TrackerData) => ({
      type: 'Feature' as const,
      geometry: {
        type: 'Point' as const,
        coordinates: [tracker.position.lng, tracker.position.lat]
      },
      properties: {
        trackerId: tracker.trackerId,
        icon: `beacon-${tracker.status}`,
        label: (tracker.title || tracker.currentRole).replace(/_/g, ' ').toUpperCase(),
        status: tracker.status,
        speed: getSpeedValue(tracker.speed || 0),
        elevation: getElevationValue(tracker.elevation || 0),
        battery: tracker.batteryLevel || 0,
        signal: tracker.signalStrength || 0,
        lastUpdate: tracker.lastUpdate
      }
    }));

    // Update the tracker source
    const source = map.current.getSource('trackers');
    if (source && source.type === 'geojson') {
      (source as mapboxgl.GeoJSONSource).setData({
        type: 'FeatureCollection',
        features
      });
    }

    // Auto camera movement (only when auto mode is enabled)
    if (autoCameraMode && visibleTrackers.length > 0) {
      const validPositions = visibleTrackers.map((t: TrackerData) => [t.position.lng, t.position.lat] as [number, number]);

      if (validPositions.length > 0) {
        if (followTracker === 'all') {
          // Follow all visible trackers
          const bounds = new mapboxgl.LngLatBounds();
          validPositions.forEach((pos: [number, number]) => bounds.extend(pos));

          if (validPositions.length === 1) {
            map.current.easeTo({
              center: validPositions[0],
              zoom: Math.max(map.current.getZoom(), 13),
              duration: 1000
            });
          } else {
            map.current.fitBounds(bounds, {
              padding: 50,
              maxZoom: 15,
              duration: 1000
            });
          }
        } else {
          // Follow specific tracker if visible
          const specificTracker = visibleTrackers.find((t: TrackerData) => t.trackerId === followTracker);
          if (specificTracker && specificTracker.position) {
            map.current.easeTo({
              center: [specificTracker.position.lng, specificTracker.position.lat],
              duration: 1000
            });
          }
        }
      }
    }
  }, [trackers, trackerVisibility, autoCameraMode, followTracker]);

  // Handle active route display
  useEffect(() => {
    if (!map.current) return;

    const routeSourceId = 'active-route';
    const routeLayerId = 'active-route-layer';

    // Remove existing route
    if (map.current.getLayer(routeLayerId)) {
      map.current.removeLayer(routeLayerId);
    }
    if (map.current.getSource(routeSourceId)) {
      map.current.removeSource(routeSourceId);
    }

    // Remove existing route markers
    if (routeMarkers.current.start) {
      routeMarkers.current.start.remove();
      routeMarkers.current.start = undefined;
    }
    if (routeMarkers.current.finish) {
      routeMarkers.current.finish.remove();
      routeMarkers.current.finish = undefined;
    }

    // Load route if available
    if (stableRouteId && activeRoute?.hasCoordinates) {
      const loadRouteCoordinates = async () => {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL}/dashboard/active-route-coordinates`);
          if (!response.ok) return;

          const result = await response.json();
          if (result.success && result.data.coordinates) {
            const coordinates = result.data.coordinates;

            const routeGeoJSON = {
              type: 'Feature' as const,
              properties: {
                name: activeRoute.raceName || 'Active Route',
                stage: activeRoute.stageName || '',
                distance: activeRoute.totalDistance
              },
              geometry: {
                type: 'LineString' as const,
                coordinates: coordinates.map((coord: any) => [coord.lng, coord.lat])
              }
            };

            if (map.current) {
              map.current.addSource(routeSourceId, {
                type: 'geojson',
                data: routeGeoJSON
              });

              map.current.addLayer({
                id: routeLayerId,
                type: 'line',
                source: routeSourceId,
                layout: {
                  'line-join': 'round',
                  'line-cap': 'round'
                },
                paint: {
                  'line-color': '#EF4444',
                  'line-width': 3,
                  'line-opacity': 1.0
                }
              });

              // Add start and finish markers
              if (coordinates.length > 0) {
                const startCoord = coordinates[0];
                const finishCoord = coordinates[coordinates.length - 1];

                const startElement = createRouteMarkerElement('start');
                routeMarkers.current.start = new mapboxgl.Marker(startElement)
                  .setLngLat([startCoord.lng, startCoord.lat])
                  .addTo(map.current!);

                const finishElement = createRouteMarkerElement('finish');
                routeMarkers.current.finish = new mapboxgl.Marker(finishElement)
                  .setLngLat([finishCoord.lng, finishCoord.lat])
                  .addTo(map.current!);
              }
            }
          }
        } catch (error) {
          console.error('Error loading route coordinates:', error);
        }
      };

      loadRouteCoordinates();
    }
  }, [stableRouteId, activeRoute?.hasCoordinates, activeRoute?.raceName, activeRoute?.stageName, activeRoute?.totalDistance]);

  // Manual camera control functions
  const centerOnAllTrackers = () => {
    if (!map.current || trackers.length === 0) return;

    const visibleTrackers = trackers.filter(tracker =>
      tracker.position &&
      tracker.position.lat &&
      tracker.position.lng &&
      trackerVisibility[tracker.trackerId] !== false
    );

    const validPositions = visibleTrackers.map(t => [t.position.lng, t.position.lat] as [number, number]);

    if (validPositions.length > 0) {
      const bounds = new mapboxgl.LngLatBounds();
      validPositions.forEach(pos => bounds.extend(pos));

      if (validPositions.length === 1) {
        map.current.easeTo({
          center: validPositions[0],
          zoom: 15,
          duration: 1000
        });
      } else {
        map.current.fitBounds(bounds, {
          padding: 50,
          maxZoom: 15,
          duration: 1000
        });
      }
    }
  };

  const centerOnTracker = (trackerId: string) => {
    if (!map.current) return;

    const tracker = trackers.find(t => t.trackerId === trackerId);
    if (tracker && tracker.position) {
      map.current.easeTo({
        center: [tracker.position.lng, tracker.position.lat],
        zoom: Math.max(map.current.getZoom(), 15),
        duration: 1000
      });
    }
  };

  const toggleTrackerVisibility = (trackerId: string) => {
    setTrackerVisibility(prev => ({
      ...prev,
      [trackerId]: !prev[trackerId]
    }));
  };

  const toggleAllTrackers = (visible: boolean) => {
    const newVisibility: Record<string, boolean> = {};
    trackers.forEach(tracker => {
      newVisibility[tracker.trackerId] = visible;
    });
    setTrackerVisibility(newVisibility);
  };

  const visibleTrackerCount = trackers.filter(tracker => trackerVisibility[tracker.trackerId] !== false).length;

  return (
    <Box sx={{ height: '100vh', width: '100vw', position: 'relative', bgcolor: '#000' }}>
      {/* Full Screen Map */}
      <div
        ref={mapContainer}
        style={{ height: '100%', width: '100%' }}
      />

      {/* Broadcast Controls Overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          gap: 1
        }}
      >
        {/* Control Toggle Buttons */}
        <Stack direction="row" spacing={1}>
          <Tooltip title={showControls ? "Hide Camera Controls" : "Show Camera Controls"}>
            <IconButton
              onClick={() => setShowControls(!showControls)}
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
                boxShadow: 2
              }}
            >
              {showControls ? <VisibilityOff /> : <Settings />}
            </IconButton>
          </Tooltip>

          <Tooltip title={showTrackerPanel ? "Hide Tracker Controls" : "Show Tracker Controls"}>
            <IconButton
              onClick={() => setShowTrackerPanel(!showTrackerPanel)}
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
                boxShadow: 2
              }}
            >
              {showTrackerPanel ? <VisibilityOff /> : <MapIcon />}
            </IconButton>
          </Tooltip>
        </Stack>

        {/* Camera Controls Panel */}
        {showControls && (
          <Card sx={{ minWidth: 280, backgroundColor: 'rgba(255, 255, 255, 0.95)' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="subtitle2" gutterBottom>
                Camera Controls
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={autoCameraMode}
                    onChange={(e) => setAutoCameraMode(e.target.checked)}
                    color="primary"
                  />
                }
                label="Auto Camera"
                sx={{ mb: 2, display: 'flex' }}
              />

              {autoCameraMode && (
                <Box sx={{ mb: 2 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Follow Tracker</InputLabel>
                    <Select
                      value={followTracker}
                      onChange={(e) => setFollowTracker(e.target.value)}
                      label="Follow Tracker"
                    >
                      <MenuItem value="all">All Visible Trackers</MenuItem>
                      {trackers.map((tracker) => (
                        <MenuItem key={tracker.trackerId} value={tracker.trackerId}>
                          {tracker.title || tracker.currentRole}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}

              {!autoCameraMode && (
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Manual Controls
                  </Typography>

                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FitScreen />}
                    onClick={centerOnAllTrackers}
                    fullWidth
                    sx={{ mb: 1 }}
                  >
                    Fit Visible Trackers ({visibleTrackerCount})
                  </Button>

                  <ButtonGroup
                    orientation="vertical"
                    variant="outlined"
                    size="small"
                    fullWidth
                  >
                    {trackers.map((tracker) => (
                      <Button
                        key={tracker.trackerId}
                        startIcon={<CenterFocusStrong />}
                        onClick={() => centerOnTracker(tracker.trackerId)}
                        sx={{ justifyContent: 'flex-start' }}
                        disabled={trackerVisibility[tracker.trackerId] === false}
                      >
                        {tracker.title || tracker.currentRole}
                      </Button>
                    ))}
                  </ButtonGroup>
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {/* Tracker Visibility Panel */}
        {showTrackerPanel && (
          <Card sx={{ minWidth: 280, backgroundColor: 'rgba(255, 255, 255, 0.95)' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="subtitle2">
                  Tracker Visibility
                </Typography>
                <Chip
                  label={`${visibleTrackerCount}/${trackers.length}`}
                  size="small"
                  color="primary"
                />
              </Stack>

              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => toggleAllTrackers(true)}
                  fullWidth
                >
                  Show All
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => toggleAllTrackers(false)}
                  fullWidth
                >
                  Hide All
                </Button>
              </Stack>

              <Divider sx={{ mb: 2 }} />

              <FormGroup>
                {trackers.map((tracker) => (
                  <FormControlLabel
                    key={tracker.trackerId}
                    control={
                      <Checkbox
                        checked={trackerVisibility[tracker.trackerId] !== false}
                        onChange={() => toggleTrackerVisibility(tracker.trackerId)}
                        size="small"
                      />
                    }
                    label={
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%' }}>
                        <Typography variant="body2">
                          {tracker.title || tracker.currentRole}
                        </Typography>
                        <Chip
                          label={tracker.status}
                          size="small"
                          color={
                            tracker.status === 'active' ? 'success' :
                            tracker.status === 'standby' ? 'warning' : 'error'
                          }
                          variant="outlined"
                        />
                      </Stack>
                    }
                    sx={{
                      margin: 0,
                      '& .MuiFormControlLabel-label': { width: '100%' }
                    }}
                  />
                ))}
              </FormGroup>
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Status Indicator */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 16,
          left: 16,
          zIndex: 1000
        }}
      >
        <Card sx={{ backgroundColor: 'rgba(0, 0, 0, 0.8)' }}>
          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold' }}>
                LIVE CG
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                {visibleTrackerCount} trackers visible
              </Typography>
              {activeRoute && (
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Route: {activeRoute.raceName}
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default BroadcastMap;